import TopCheckoutbanner from '@/assets/icons/TopCheckoutbanner'
import { Label } from '@/components/shared/Label/Label'
import { Input as FileInput } from '@/components/ui/input'
import { TabsContent } from '@/components/ui/tabs'
import WhatsIcon from '@/assets/icons/WhatsIcon'
import { useRef } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import { handleProductPhotoFileChange } from '@/utils/customizeCheckout'
import { CheckoutHeaderPreview } from '../CheckoutHeaderPreview'

type ProductPhotoTabProps = {
  values: CustomizeCheckoutData
  setValue: UseFormSetValue<CustomizeCheckoutData>
  productPhotoPreviewUrl: string | null
  checkoutName?: string
  checkoutPrice?: string
}

export function ProductPhotoTab({
  values,
  setValue,
  productPhotoPreviewUrl,
  checkoutName,
  checkoutPrice,
}: ProductPhotoTabProps) {
  const { t } = useTranslation(['common', 'checkouts'])
  const productPhotoInputRef = useRef<HTMLInputElement>(null)

  const hasProductPhoto =
    !values.remove_product_photo &&
    Boolean(
      values.product_photo instanceof File ||
        (typeof values.product_photo === 'string' && values.product_photo),
    )

  const handleRemoveProductPhoto = () => {
    setValue('product_photo', null)
    setValue('remove_product_photo', true)
    if (productPhotoInputRef.current) {
      productPhotoInputRef.current.value = ''
    }
  }

  return (
    <TabsContent
      value="product-photo"
      className="mt-8 flex-col justify-between gap-12 lg:flex-row xl:flex-col"
    >
      <section className="flex w-full grid-cols-2 flex-col items-start gap-6 md:grid md:gap-12 xl:grid-cols-1 2xl:grid-cols-2">
        <section className="w-full space-y-4 lg:max-w-[402px] xl:max-w-none">
          <div>
            <Label className="text-lg font-semibold">
              {t('checkouts:product_photo_title')}
            </Label>
            <p className="text-muted-foreground text-sm mt-1.5">
              {t('checkouts:product_photo_helper')}
            </p>
          </div>

          <div>
            <article className="text-muted mb-2 text-sm">
              {t('checkouts:file_recommendations', {
                size: 4,
                width: 500,
                height: 500,
              })}
            </article>
            <FileInput
              ref={productPhotoInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="text-muted"
              data-cy="input-product-photo"
              onChange={(event) =>
                handleProductPhotoFileChange(event, setValue, t)
              }
            />
            {hasProductPhoto && (
              <button
                type="button"
                className="mt-3 text-sm text-red-400 hover:text-red-300"
                data-cy="btn-remove-product-photo"
                onClick={handleRemoveProductPhoto}
              >
                {t('checkouts:product_photo_remove')}
              </button>
            )}
          </div>
        </section>
        <section className="relative hidden h-[400px] w-full rounded-2xl bg-[#18181B] md:block xl:hidden">
          <article className="flex justify-between items-center p-2">
            <TopCheckoutbanner />
            <p className="text-xs font-medium text-muted">go.mundpay.com</p>
            <p>&nbsp;</p>
          </article>
          <div className="flex gap-1 items-center flex-col">
            <CheckoutHeaderPreview
              src={productPhotoPreviewUrl}
              name={values?.name || checkoutName}
              price={checkoutPrice}
            />
            <p className="text-center text-[10px] text-zinc-500 px-3">
              {t('checkouts:product_photo_preview_caption')}
            </p>
          </div>
          {Boolean(values?.show_whatsapp_support) && (
            <button
              type="button"
              aria-label={t('common:whatsapp')}
              className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center"
            >
              <WhatsIcon className="h-5 w-5" />
            </button>
          )}
        </section>
      </section>
    </TabsContent>
  )
}
